import { ShowList, ShowListSkeleton } from "@picovico/ui";
import {
  isShowBookable,
  isShowViewable,
} from "@picovico/ui/constant/enums";
import {
  convertTimeTo12HourFormat,
  getLocalStorageData,
  storeLocalStorageData,
} from "@picovico/utils";
import { useEffect, useState } from "react";

import { BoxProps, ButtonProps, FlexProps, TextProps } from "@chakra-ui/react";
import { DateSelectorProps } from "@picovico/ui/modules/DateSelector/DateSelector";
import { useLazyGetShowDetailListQuery } from "@store/services/movie";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { getPageRoutes } from "routes/pageRoutes";
import { useAppSelector } from "store";
import {
  ShowNoteCardComponent1,
  ShowNoteCardLegendItem,
} from "./ShowNoteCardComponent1";

export function ShowListContainer({
  dateSelectorProps,
  showButtonProps,
  videoQualityBadgeProps,
  legendItems,
  legendContainerProps,
  legendItemProps,
  legendDotProps,
  legendTextProps,
  ...props
}: {
  dateSelectorProps: DateSelectorProps;
  showButtonProps?: ButtonProps;
  videoQualityBadgeProps?: TextProps;
  legendItems?: ShowNoteCardLegendItem[];
  legendContainerProps?: FlexProps;
  legendItemProps?: FlexProps;
  legendDotProps?: BoxProps;
  legendTextProps?: TextProps;
}) {
  const router = useRouter();
  const movieId = router?.query?.movieIdx;
  const cinemaHallIdx = router?.query?.cinemaHallIdx;
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );

  const { advanceShowDays } = useAppSelector((state) => state.cineSetting);
  const [
    getShowList,
    { data: showList, isLoading: isLoadingShowList, isFetching: isFetchingShowList },
  ] = useLazyGetShowDetailListQuery({});
  const selectedHall = getLocalStorageData("cinema_hall") ?? cinemaHallIdx;

  const startDate = router.query.startDate as string;
  const endDate = router.query.endDate as string;

  useEffect(() => {
    if (cinemaHallIdx) {
      storeLocalStorageData("cinema_hall", cinemaHallIdx);
    }
    if (router.query.date) {
      setSelectedDate(
        format(new Date(router.query.date as string), "yyyy-MM-dd"),
      );
    } else if (startDate) {
      setSelectedDate(format(new Date(startDate), "yyyy-MM-dd"));
    }
  }, [router, startDate, cinemaHallIdx]);

  useEffect(() => {
    if (movieId && selectedHall) {
      getShowList({
        cinema_hall: selectedHall,
        show_date: selectedDate,
        movie: movieId,
      });
    }
  }, [selectedDate, selectedHall, getShowList, movieId]);

  if (!movieId) return null;

  return (
    <div>
      {isLoadingShowList || isFetchingShowList ? (
        <ShowListSkeleton />
      ) : (
        showList && (
          <>
            {showList.data[0]?.has_show && legendItems && legendItems.length > 0 && (
              <ShowNoteCardComponent1
                legendItems={legendItems}
                legendContainerProps={legendContainerProps}
                legendItemProps={legendItemProps}
                legendDotProps={legendDotProps}
                legendTextProps={legendTextProps}
              />
            )}
            <ShowList
            {...props}
            dateSelectorProps={{
              startDate: startDate ? new Date(startDate) : undefined,
              endDate: endDate ? new Date(endDate) : undefined,
              currentDate: selectedDate ? new Date(selectedDate) : undefined,
              ...dateSelectorProps,
            }}
            dateSelectorMaxDays={advanceShowDays}
            onDateChange={(date) => {
              setSelectedDate(format(date, "yyyy-MM-dd"));
              getShowList({
                cinema_hall: selectedHall,
                show_date: format(date, "yyyy-MM-dd"),
                movie: movieId,
              });
            }}
            has_show={showList?.data[0].has_show}
            screens={
              showList?.data[0]?.screens?.map((screen) => {
                return {
                  name: screen.screen_name,
                  shows: screen.showinfo?.map((show) => ({
                    startTime: convertTimeTo12HourFormat(
                      show.show_start_date_time.slice(11, 16),
                    ),
                    show_status: show.show_status,
                    // A locked or already-started show stays reachable so the
                    // seat layout can be viewed; booking is blocked on the seat
                    // page instead.
                    lockWeb: show.lock_web,
                    isAvailable: isShowBookable(show.show_status),
                    isViewable: isShowViewable(
                      show.show_status,
                      show.show_ended,
                      show.seat_view_allowed,
                    ),
                    showDetailLink: getPageRoutes(
                      "audi_detail",
                      {
                        movieIdx: movieId as string,
                        showIdx: show.show_idx as string,
                      },
                      {
                        prev: router.asPath,
                      },
                    ),
                    videoQuality: show.video_quality,
                    showButtonProps,
                    videoQualityBadgeProps,
                  })),
                };
              }) || []
            }
          />
          </>
        )
      )}
    </div>
  );
}
